import React, { useEffect, useState, useRef } from 'react';
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  IconButton,
  Divider,
  Avatar,
  useMediaQuery,
  useTheme,
  Badge,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Stack,
} from '@mui/material';
import {
  ChevronLeft,
  ChevronRight,
  Dashboard,
  People,
  AdminPanelSettings,
  Logout,
  Menu,
  EmailOutlined,
  Phone,
  PhoneMissed,
  Settings,
  Schedule,
  Error as ErrorIcon,
  Cancel,
  ExpandLess,
  ExpandMore,
} from '@mui/icons-material';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import { useAuthUser, useSignOut } from "react-auth-kit";
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { logoutApi, getEmailQueueStatusApi, allUsersApi, getCallQueueStatusApi, getCallStatisticsApi, AssignAdminToManagerApi } from '../../../Api/Service';
import { toast } from 'react-toastify';
import io from 'socket.io-client';

const Sidebar = ({ isCollapsed, setIsSidebarCollapsed, isMobileMenu, setisMobileMenu }) => {
  const user = useAuthUser();
  const signOut = useSignOut();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [drawerVariant, setDrawerVariant] = useState(isMobile ? "temporary" : "permanent");
  const [emailQueueCount, setEmailQueueCount] = useState(0);
  const [pendingCallsCount, setPendingCallsCount] = useState(0);
  const [failedCallsCount, setFailedCallsCount] = useState(0);
  const [noAnswerCallsCount, setNoAnswerCallsCount] = useState(0);
  const [cancelledCallsCount, setCancelledCallsCount] = useState(0);
  const [callMenuOpen, setCallMenuOpen] = useState(false);
  const [internalMobileMenu, setInternalMobileMenu] = useState(false);
  const [currentUserLatest, setCurrentUserLatest] = useState(null);
  const previousPathRef = useRef(location.pathname);
  
  // Assignment dialog state for managers
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [assignEmail, setAssignEmail] = useState("");
  
  // Use internal state if setisMobileMenu not provided
  const mobileMenuState = isMobileMenu ?? internalMobileMenu;
  const setMobileMenuState = setisMobileMenu ?? setInternalMobileMenu;

  // ✅ CRITICAL: Fetch latest user with permissions in real-time (not from stale auth-kit data)
  // This ensures permissions are checked against the database, not cached JWT data
  const fetchUserPermissions = async () => {
    try {
      const u = user()?.user;
      if (!u?._id) return;
      // Fetch fresh user data from database to get latest permissions
      const resp = await allUsersApi({ search: u._id, limit: 1 });
      if (resp.success && resp.allUsers?.length) {
        setCurrentUserLatest(resp.allUsers[0]);
      } else {
        setCurrentUserLatest(u);
      }
    } catch (e) {
      // Non-blocking; fall back to token payload
      setCurrentUserLatest(user()?.user || null);
    }
  };

  useEffect(() => {
    fetchUserPermissions();
    // Refresh permissions every 30 seconds to catch permission changes in real-time
    const interval = setInterval(fetchUserPermissions, 30000);
    return () => clearInterval(interval);
  }, []);

  // ✅ Socket.io for real-time email queue updates
  useEffect(() => {
    // Use effectiveUser role (from live database) instead of auth-kit storage
    const effectiveUserRole = (currentUserLatest || user()?.user)?.role;
    if (effectiveUserRole !== 'superadmin') return;

    // Connect to Socket.io - Use same backend as API calls 
    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:4000/api';
    
    // Parse URL properly to avoid malformed URLs
    let backendUrl;
    try {
      const url = new URL(apiUrl);
      backendUrl = `${url.protocol}//${url.host}`; // Get protocol + host only
    } catch (e) {
      console.error('Invalid REACT_APP_API_URL:', apiUrl);
      // Use production API if localhost fails
      backendUrl = window.location.hostname === 'localhost' 
        ? 'http://localhost:4000' 
        : 'https://api.callmind.tech';
    }
    
    // If still localhost but not running locally, use production
    if (backendUrl.includes('localhost') && window.location.hostname !== 'localhost') {
      backendUrl = 'https://api.callmind.tech';
    }
     
    
    const socket = io(backendUrl, {
      withCredentials: true,
      transports: ['websocket', 'polling'] // Better compatibility
    });

    socket.on('connect', () => {});

    socket.on('connect_error', (error) => { 
    });

    // Listen for email queue updates
    socket.on('emailQueueUpdate', (data) => {const totalCount = (data.pending || 0) + (data.failed || 0);
      setEmailQueueCount(totalCount);
    });

    // Fetch initial status
    getEmailQueueStatusApi().then(response => {
      if (response.success) {
        const totalCount = (response.data.pending || 0) + (response.data.failed || 0);
        setEmailQueueCount(totalCount);}
    }).catch(err => {
      console.error('Error fetching email queue status:', err);
    });

    return () => {socket.disconnect();
    };
  }, [currentUserLatest]);

  // Build menu based on permissions (focus: restricting admins)
  const effectiveUser = currentUserLatest || user()?.user;
  const role = effectiveUser?.role;
  const isSuperadmin = role === 'superadmin';
  const isAdmin = role === 'admin';
  const isSubadmin = role === 'subadmin';
  const isManager = role === 'manager';

  // ✅ CRITICAL: Strict permission check - must be explicitly true (not just truthy)
  // This ensures permissions are enforced in real-time from database
  // Manager must have explicit accessCrm permission (same as admin)
  const hasCrmAccess = Boolean(
    isSuperadmin ||
    (isManager && (effectiveUser?.adminPermissions?.accessCrm === true || effectiveUser?.adminPermissions?.accessCrm === 'true')) ||
    (isAdmin && (effectiveUser?.adminPermissions?.accessCrm === true || effectiveUser?.adminPermissions?.accessCrm === 'true')) ||
    (isSubadmin && (effectiveUser?.permissions?.accessCrm === true || effectiveUser?.permissions?.accessCrm === 'true'))
  );

  // ✅ CRITICAL: Strict permission check for viewing/managing Leads
  // Must have canManageCrmLeads permission (not just accessCrm)
  const canManageCrmLeads = Boolean(
    isSuperadmin ||
    (isManager && (effectiveUser?.adminPermissions?.canManageCrmLeads === true || effectiveUser?.adminPermissions?.canManageCrmLeads === 'true')) ||
    (isAdmin && (effectiveUser?.adminPermissions?.canManageCrmLeads === true || effectiveUser?.adminPermissions?.canManageCrmLeads === 'true')) ||
    (isSubadmin && (effectiveUser?.permissions?.canManageCrmLeads === true || effectiveUser?.permissions?.canManageCrmLeads === 'true'))
  );

  // ✅ CRITICAL: Strict permission check for Call Dashboard
  // Manager must have explicit canAccessCallDashboard permission (same as admin)
  // Admins need explicit permission
  const canAccessCallDashboard = Boolean(
    isSuperadmin || 
    (isManager && (effectiveUser?.adminPermissions?.canAccessCallDashboard === true || effectiveUser?.adminPermissions?.canAccessCallDashboard === 'true')) ||
    (isAdmin && (effectiveUser?.adminPermissions?.canAccessCallDashboard === true || effectiveUser?.adminPermissions?.canAccessCallDashboard === 'true')) ||
    (isSubadmin && (effectiveUser?.permissions?.canAccessCallDashboard === true || effectiveUser?.permissions?.canAccessCallDashboard === 'true'))
  );

  // ✅ CRITICAL: Strict permission check for Profile Update
  // Must have isProfileUpdate permission
  const canUpdateProfile = Boolean(
    isSuperadmin ||
    (isManager && (effectiveUser?.adminPermissions?.isProfileUpdate === true || effectiveUser?.adminPermissions?.isProfileUpdate === 'true')) ||
    (isAdmin && (effectiveUser?.adminPermissions?.isProfileUpdate === true || effectiveUser?.adminPermissions?.isProfileUpdate === 'true')) ||
    (isSubadmin && (effectiveUser?.permissions?.isProfileUpdate === true || effectiveUser?.permissions?.isProfileUpdate === 'true'))
  );

  // ✅ Fetch pending and failed calls counts for badges
  useEffect(() => {
    if (!canAccessCallDashboard) return;

    const fetchCallCounts = async () => {
      try {
        const [queueRes, statsRes] = await Promise.all([
          getCallQueueStatusApi({ scheduledPage: 1, scheduledLimit: 1 }),
          getCallStatisticsApi()
        ]);

        if (queueRes.success && queueRes.queue?.scheduledPagination) {
          setPendingCallsCount(queueRes.queue.scheduledPagination.total || 0);
        }

        if (statsRes.success && statsRes.statistics?.byStatus) {
          const failedStats = statsRes.statistics.byStatus.find(s => s._id === 'failed');
          if (failedStats) {
            setFailedCallsCount(failedStats.count || 0);
          }
          const noAnswerStats = statsRes.statistics.byStatus.find(s => s._id === 'no-answer');
          if (noAnswerStats) {
            setNoAnswerCallsCount(noAnswerStats.count || 0);
          }
          const cancelledStats = statsRes.statistics.byStatus.find(s => s._id === 'cancelled');
          if (cancelledStats) {
            setCancelledCallsCount(cancelledStats.count || 0);
          }
        }
      } catch (error) {
        console.error('Error fetching call counts:', error);
      }
    };

    fetchCallCounts();
    // Refresh every 10 seconds
    const interval = setInterval(fetchCallCounts, 10000);
    return () => clearInterval(interval);
  }, [canAccessCallDashboard, currentUserLatest]);

  // ✅ CRITICAL: Strict permission check for AI Instructions
  // Only superadmin, admin, and manager can access (subadmin excluded per backend routes)
  const canAccessAiInstructions = Boolean(
    isSuperadmin ||
    (isManager && (effectiveUser?.adminPermissions?.canAccessAiInstructions === true || effectiveUser?.adminPermissions?.canAccessAiInstructions === 'true')) ||
    (isAdmin && (effectiveUser?.adminPermissions?.canAccessAiInstructions === true || effectiveUser?.adminPermissions?.canAccessAiInstructions === 'true'))
  );

  const menuItems = [];
  // ✅ CRITICAL: Only show Leads if user has canManageCrmLeads permission (not just accessCrm)
  // This ensures users can only see leads if they have the specific permission to manage/view leads
  if (canManageCrmLeads === true) {
    // menuItems.push({ icon: <Dashboard />, label: 'Dashbaord', link: "/admin/dashboard" });
    menuItems.push({ icon: <People />, label: 'Leads', link: "/admin/dashboard/crm" });
  }
  
  // Call-related menu items (will be in dropdown)
  // Only populate if user has explicit access
  const callMenuItems = [];
  if (canAccessCallDashboard === true) {
    callMenuItems.push({ icon: <Phone />, label: 'Active Calls', link: "/admin/crm/call-dashboard" });
    callMenuItems.push({ icon: <Schedule />, label: 'Pending Calls Queue', link: "/admin/crm/pending-calls", badge: true, badgeCount: pendingCallsCount });
    callMenuItems.push({ icon: <ErrorIcon />, label: 'Failed Calls', link: "/admin/crm/failed-calls", badge: true, badgeCount: failedCallsCount });
    callMenuItems.push({ icon: <PhoneMissed />, label: 'No Answer Calls', link: "/admin/crm/no-answer-calls", badge: true, badgeCount: noAnswerCallsCount });
    callMenuItems.push({ icon: <Cancel />, label: 'Cancelled Calls', link: "/admin/crm/cancelled-calls", badge: true, badgeCount: cancelledCallsCount });
  }

  useEffect(() => {
    // If user doesn't have access, close menu and return early
    if (canAccessCallDashboard !== true || callMenuItems.length === 0) {
      setCallMenuOpen(false);
      previousPathRef.current = location.pathname;
      return;
    }

    const isCallRoute = (path) => callMenuItems.some(item => path.startsWith(item.link));
    const currentlyCallRoute = isCallRoute(location.pathname);
    const previouslyCallRoute = isCallRoute(previousPathRef.current);

    if (currentlyCallRoute) {
      setCallMenuOpen(true);
    } else if (previouslyCallRoute) {
      setCallMenuOpen(false);
    }

    previousPathRef.current = location.pathname;
  }, [location.pathname, canAccessCallDashboard, callMenuItems.length]);



  // Admin Management (CRM) - superadmin and managers
  if ((isSuperadmin && effectiveUser?.role === 'superadmin') || (isManager && effectiveUser?.role === 'manager')) {
    menuItems.push({ 
      icon: <AdminPanelSettings />, 
      label: isSuperadmin ? 'Admin Management' : 'Manage My Bots', 
      link: "/admin/crm/admin-management" 
    });
  }

  // Add Recycle Bin and Email Queue for superadmin (check using effectiveUser from database)
  if (effectiveUser?.role === 'superadmin') {
    menuItems.push({ icon: <DeleteForeverIcon />, label: 'Recycle Bin', link: "/admin/dashboard/crm/recycle-bin" });
    menuItems.push({ icon: <EmailOutlined />, label: 'Email Queue', link: "/admin/crm/email-queue", badge: true });
  }

  // ✅ CRITICAL: Add Profile link only if user has isProfileUpdate permission
  // Check using effectiveUser (live database data) instead of auth-kit storage
  if (canUpdateProfile === true) {
    menuItems.push({ icon: <Settings />, label: 'My Profile', link: "/admin/crm/profile" });
  }

  // Handle assign admin for managers
  const handleAssignAdmin = async () => {
    if (!assignEmail || !assignEmail.trim()) {
      toast.error('Please enter admin email');
      return;
    }

    setAssigning(true);
    try {
      const res = await AssignAdminToManagerApi({ email: assignEmail.trim() });
      
      if (res && res.success) {
        toast.success(res.msg || 'Admin assigned successfully');
        setAssignDialogOpen(false);
        setAssignEmail("");
      } else {
        toast.error(res?.msg || res?.message || 'Failed to assign admin');
      }
    } catch (error) {
      console.error('Assign error:', error);
      toast.error(error?.response?.data?.msg || error?.msg || error?.message || 'Failed to assign admin. Please try again.');
    } finally {
      setAssigning(false);
    }
  };

  const isActiveLink = (link) => location.pathname === link;

  const handleLogout = async () => {
    try {
      const logout = await logoutApi();
      if (logout.success) {
        signOut();
        navigate("/auth/login/crm");
      } else {
        toast.error(logout.msg);
      }
    } catch (error) {
      toast.error(error.message || "Logout failed");
    }
  };

  const drawerWidth = isCollapsed ? 80 : 280;

  // Handle responsive behavior
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768) {
        setDrawerVariant("permanent");
        if (setMobileMenuState) {
          setMobileMenuState(false);
        }
      } else {
        setDrawerVariant("temporary");
      }
    };

    window.addEventListener('resize', handleResize);
    handleResize(); // Initialize on mount

    return () => window.removeEventListener('resize', handleResize);
  }, [setMobileMenuState]);

  return (
    <Drawer
      variant={drawerVariant}
      open={isMobile ? mobileMenuState : true}
      onClose={() => setMobileMenuState && setMobileMenuState(false)}
      sx={{
        width: { xs: 280, md: drawerWidth },
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: { xs: 280, md: drawerWidth },
          boxSizing: 'border-box',
          border: 'none',
          boxShadow: '0 0 20px rgba(0,0,0,0.1)',
          transition: 'width 0.3s ease, transform 0.3s ease',
          overflowX: 'hidden',
        },
      }}
    >
      {/* Header / Logo */}
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          {!isCollapsed && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Avatar sx={{ bgcolor: 'primary.main', width: 32, height: 32 }}>C</Avatar>
              <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'text.primary' }}>
             Callmind Tech
              </Typography>
            </Box>
          )}
          {isCollapsed && (
            <Avatar sx={{ bgcolor: 'primary.main', width: 32, height: 32, mx: 'auto' }}>C</Avatar>
          )}
          <Box>
            {/* Desktop collapse toggle */}
            <IconButton
              onClick={() => setIsSidebarCollapsed(!isCollapsed)}
              size="small"
              sx={{ color: 'text.secondary', display: { xs: 'none', md: 'inline-flex' } }}
            >
              {isCollapsed ? <ChevronRight /> : <ChevronLeft />}
            </IconButton>
            {/* Mobile menu toggle */}
            <IconButton
              onClick={() => setMobileMenuState && setMobileMenuState(false)}
              size="small"
              sx={{ color: 'text.secondary', display: { xs: 'inline-flex', md: 'none' } }}
            >
              <Menu />
            </IconButton>
          </Box>
        </Box>
      </Box>

      {/* Menu Items */}
      <Box sx={{ flex: 1, p: 1, overflow: 'auto' }}>
        <List>
          {menuItems.map((item, index) => {
            // Insert Call Dashboard dropdown after the first item (Leads)
            // Only show call menu if user has access AND there are call menu items
            const shouldShowCallMenu = index === 0 && canAccessCallDashboard === true && callMenuItems.length > 0;
            
            return (
              <React.Fragment key={index}>
                <ListItem disablePadding sx={{ mb: 0.5 }}>
                  <ListItemButton
                    component={Link}
                    to={item.link}
                    onClick={() => isMobile && setMobileMenuState && setMobileMenuState(false)}
                    sx={{
                      borderRadius: 2,
                      color: isActiveLink(item.link) ? 'primary.main' : 'text.secondary',
                      backgroundColor: isActiveLink(item.link) ? 'action.selected' : 'transparent',
                      '&:hover': { 
                        backgroundColor: isActiveLink(item.link) ? 'action.selected' : 'action.hover' 
                      },
                      justifyContent: isCollapsed ? 'center' : 'flex-start',
                      px: 2,
                      py: 1.5,
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: 'auto',
                        color: 'inherit',
                        mr: isCollapsed ? 0 : 2,
                      }}
                    >
                      {item.badge && (item.badgeCount > 0 || emailQueueCount > 0) ? (
                        <Badge 
                          badgeContent={item.badgeCount !== undefined ? item.badgeCount : emailQueueCount} 
                          color={item.link?.includes('failed') ? 'error' : 'warning'} 
                          max={99}
                        >
                          {item.icon}
                        </Badge>
                      ) : (
                        item.icon
                      )}
                    </ListItemIcon>
                    {!isCollapsed && (
                      <ListItemText
                        primary={item.label}
                        primaryTypographyProps={{
                          fontSize: '0.875rem',
                          fontWeight: isActiveLink(item.link) ? 600 : 500,
                        }}
                      />
                    )}
                    {!isCollapsed && item.badge && (item.badgeCount > 0 || emailQueueCount > 0) && (
                      <Badge 
                        badgeContent={item.badgeCount !== undefined ? item.badgeCount : emailQueueCount} 
                        color={item.link?.includes('failed') ? 'error' : 'warning'} 
                        max={99}
                      />
                    )}
                  </ListItemButton>
                </ListItem>
                
                {/* Call Dashboard Dropdown Menu - Inserted after first item (Leads) */}
                {/* Only render if user has explicit access AND menu items exist */}
                {canAccessCallDashboard === true && callMenuItems.length > 0 && shouldShowCallMenu && (
                  <>
                    <ListItem disablePadding sx={{ mb: 0.5 }}>
                      <ListItemButton
                        onClick={() => !isCollapsed && setCallMenuOpen(prev => { const next=!prev; localStorage.setItem('call-menu-open',JSON.stringify(next)); return next; })}
                        sx={{
                          borderRadius: 2,
                          color: (callMenuItems.some(item => isActiveLink(item.link))) ? 'primary.main' : 'text.secondary',
                          backgroundColor: (callMenuItems.some(item => isActiveLink(item.link))) ? 'action.selected' : 'transparent',
                          '&:hover': { 
                            backgroundColor: 'action.hover' 
                          },
                          justifyContent: isCollapsed ? 'center' : 'flex-start',
                          px: 2,
                          py: 1.5,
                        }}
                      >
                        <ListItemIcon
                          sx={{
                            minWidth: 'auto',
                            color: 'inherit',
                            mr: isCollapsed ? 0 : 2,
                          }}
                        >
                          <Phone />
                        </ListItemIcon>
                        {!isCollapsed && (
                          <>
                            <ListItemText
                              primary="Call Dashboard"
                              primaryTypographyProps={{
                                fontSize: '0.875rem',
                                fontWeight: (callMenuItems.some(item => isActiveLink(item.link))) ? 600 : 500,
                              }}
                            />
                            {callMenuOpen ? <ExpandLess /> : <ExpandMore />}
                          </>
                        )}
                      </ListItemButton>
                    </ListItem>
                    <Collapse in={callMenuOpen && !isCollapsed} timeout="auto" unmountOnExit>
                      <List component="div" disablePadding>
                        {callMenuItems.map((item, itemIndex) => {
                          const isActive = isActiveLink(item.link);
                          return (
                            <ListItem key={itemIndex} disablePadding sx={{ mb: 0.5, pl: 4 }}>
                              <ListItemButton
                                component={Link}
                                to={item.link}
                                onClick={() => isMobile && setMobileMenuState && setMobileMenuState(false)}
                                sx={{
                                  borderRadius: 2,
                                  color: isActive ? 'primary.main' : 'text.secondary',
                                  backgroundColor: isActive ? 'action.selected' : 'transparent',
                                  '&:hover': { 
                                    backgroundColor: isActive ? 'action.selected' : 'action.hover' 
                                  },
                                  px: 2,
                                  py: 1.5,
                                }}
                              >
                                <ListItemIcon
                                  sx={{
                                    minWidth: 'auto',
                                    color: 'inherit',
                                    mr: 2,
                                  }}
                                >
                                  {item.badge && item.badgeCount > 0 ? (
                                    <Badge 
                                      badgeContent={item.badgeCount} 
                                      color={item.link?.includes('failed') ? 'error' : item.link?.includes('no-answer') ? 'warning' : 'default'} 
                                      max={99}
                                    >
                                      {item.icon}
                                    </Badge>
                                  ) : (
                                    item.icon
                                  )}
                                </ListItemIcon>
                                <ListItemText
                                  primary={item.label}
                                  primaryTypographyProps={{
                                    fontSize: '0.875rem',
                                    fontWeight: isActive ? 600 : 500,
                                  }}
                                />
                                {item.badge && item.badgeCount > 0 && (
                                  <Badge 
                                    badgeContent={item.badgeCount} 
                                    color={item.link?.includes('failed') ? 'error' : item.link?.includes('no-answer') ? 'warning' : 'default'} 
                                    max={99}
                                  />
                                )}
                              </ListItemButton>
                            </ListItem>
                          );
                        })}
                      </List>
                    </Collapse>
                  </>
                )}
              </React.Fragment>
            );
          })}
        </List>
      </Box>

      {/* Assign Admin Button for Managers */}
      {isManager && effectiveUser?.role === 'manager' && (
        <Box sx={{ p: 1 }}>
          <List>
            <ListItem disablePadding>
              <ListItemButton
                onClick={() => setAssignDialogOpen(true)}
                sx={{
                  borderRadius: 2,
                  justifyContent: isCollapsed ? 'center' : 'flex-start',
                  px: 2,
                  py: 1.5,
                  color: 'white',
                  backgroundColor: 'rgba(0, 0, 0, 0.1)',
                  '&:hover': { 
                    backgroundColor: 'rgba(25, 118, 210, 0.2)',
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 'auto',
                    color: 'inherit',
                    mr: isCollapsed ? 0 : 2,
                  }}
                >
                  <People />
                </ListItemIcon>
                {!isCollapsed && (
                  <ListItemText
                    primary="Assign Bot"
                    primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: 600 }}
                  />
                )}
              </ListItemButton>
            </ListItem>
          </List>
        </Box>
      )}

      <Divider />

      {/* Bottom Section - Logout */}
      <Box sx={{ p: 1 }}>
        <List>
          <ListItem disablePadding>
            <ListItemButton
              onClick={handleLogout}
              sx={{
                borderRadius: 2,
                justifyContent: isCollapsed ? 'center' : 'flex-start',
                px: 2,
                py: 1.5,
                color: 'text.secondary',
                '&:hover': { 
                  backgroundColor: 'error.light',
                  color: 'error.main'
                },
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 'auto',
                  color: 'inherit',
                  mr: isCollapsed ? 0 : 2,
                }}
              >
                <Logout />
              </ListItemIcon>
              {!isCollapsed && (
                <ListItemText
                  primary="Logout"
                  primaryTypographyProps={{ fontSize: '0.875rem' }}
                />
              )}
            </ListItemButton>
          </ListItem>
        </List>
      </Box>

      {/* User Profile */}
      {!isCollapsed && (
        <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}>
              {'A'}
            </Avatar>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }} noWrap>
                {effectiveUser?.firstName || user()?.user?.firstName}{" "}{effectiveUser?.lastName || user()?.user?.lastName}
                
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }} noWrap>
                {effectiveUser?.email || user()?.user?.email}
              </Typography>
            </Box>
          </Box>
        </Box>
      )}

      {/* Assign Admin Dialog for Managers */}
      <Dialog 
        open={assignDialogOpen} 
        onClose={() => !assigning && setAssignDialogOpen(false)} 
        maxWidth="sm" 
        fullWidth
      >
        <DialogTitle>Assign Bot to Me</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              autoFocus
              margin="dense"
              label="Admin Email"
              type="email"
              fullWidth
              variant="outlined"
              value={assignEmail}
              onChange={(e) => setAssignEmail(e.target.value)}
              placeholder="Enter admin email address"
              helperText="Enter the email of an unassigned admin to assign them to you"
              sx={{ mt: 2 }}
            />
            <Box sx={{ 
              p: 2, 
              borderRadius: 1,
              bgcolor: 'rgba(25, 118, 210, 0.1)',
              border: '1px solid',
              borderColor: 'info.main'
            }}>
              <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 500, mb: 0.5 }}>
                ℹ️ Important Notes:
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                • Only unassigned admins can be assigned
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                • If an admin is already assigned, you must contact superadmin to unassign them first
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                • The admin will be assigned to your account
              </Typography>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button 
            onClick={() => {
              setAssignDialogOpen(false);
              setAssignEmail("");
            }} 
            disabled={assigning}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleAssignAdmin} 
            variant="contained" 
            disabled={assigning || !assignEmail.trim()}
            sx={{ minWidth: 100 }}
          >
            {assigning ? 'Assigning...' : 'Assign'}
          </Button>
        </DialogActions>
      </Dialog>
    </Drawer>
  );
};

export default Sidebar;
